import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react'

interface NetworkInfo {
    id: string | null
    name: string | null
    level: string | null
    wave: string | null
}

export interface HospitalRef {
    id: string
    name: string
}

interface AuthState {
    loading: boolean
    authenticated: boolean
    username: string | null
    projectId: string | null
    dataAccessGroupName: string | null
    hospitalId: string | null
    hospitalName: string | null
    network: NetworkInfo | null
    // Sites a practice admin may access (Table[2] from the auth SP). Empty for
    // single-site users. See getAuthorizedHospitalIds for how this widens access.
    practiceAdminHospitals: HospitalRef[]
    supported: boolean
    app: string | null
    title: string | null
    defaultPath: string | null
    // Dev-only: a re-login is available after logging off (dev auto-login is suppressed
    // once the user explicitly logs off). Always false in production.
    devLoginAvailable: boolean
}

// The full set of hospital IDs the current user may view: a practice admin's
// assigned sites (plus their validated site), or just the single validated site.
export function getAuthorizedHospitalIds(
    auth: Pick<AuthState, 'hospitalId' | 'practiceAdminHospitals'>
): string[] {
    const ids = auth.practiceAdminHospitals.length > 0
        ? [auth.hospitalId, ...auth.practiceAdminHospitals.map((h) => h.id)]
        : [auth.hospitalId]
    return Array.from(new Set(ids.filter((id): id is string => !!id)))
}

interface AuthContextType extends AuthState {
    refresh: () => Promise<void>
    logout: () => Promise<void>
    // Resolves true when the dev login created a session, false when it was rejected
    // (e.g. the configured dev user has no rights to the project).
    devLogin: () => Promise<boolean>
}

const defaultState: AuthState = {
    loading: true,
    authenticated: false,
    username: null,
    projectId: null,
    dataAccessGroupName: null,
    hospitalId: null,
    hospitalName: null,
    network: null,
    practiceAdminHospitals: [],
    supported: false,
    app: null,
    title: null,
    defaultPath: null,
    devLoginAvailable: false,
}

export const AuthContext = createContext<AuthContextType>({
    ...defaultState,
    refresh: async () => { },
    logout: async () => { },
    devLogin: async () => false,
})

interface AuthProviderProps {
    children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [state, setState] = useState<AuthState>(defaultState)

    const refresh = useCallback(async () => {
        try {
            const response = await fetch('/api/auth/session')
            const data = await response.json()
            if (data.authenticated) {
                setState({
                    loading: false,
                    authenticated: true,
                    username: data.username,
                    // Normalize to a string: RequireAuth compares this strictly against the
                    // string route ids (e.g. "199"), and REDCap can return a numeric project_id.
                    projectId: data.projectId != null ? String(data.projectId) : null,
                    dataAccessGroupName: data.dataAccessGroupName,
                    hospitalId: data.hospitalId,
                    hospitalName: data.hospitalName || null,
                    network: data.network || null,
                    practiceAdminHospitals: data.practiceAdminHospitals || [],
                    supported: data.supported,
                    app: data.app,
                    title: data.title,
                    defaultPath: data.defaultPath,
                    devLoginAvailable: false,
                })
            } else {
                setState({ ...defaultState, loading: false, devLoginAvailable: !!data.devLogin })
            }
        } catch {
            setState({ ...defaultState, loading: false })
        }
    }, [])

    const logout = useCallback(async () => {
        let callbackUrl: string | null = null
        try {
            const response = await fetch('/api/auth/logout', { method: 'POST' })
            const data = await response.json().catch(() => ({}))
            callbackUrl = data?.callbackUrl || null
        } catch {
            // Network error hitting logout -- fall through to a local refresh below.
        }
        // Real REDCap/ReportPortal sessions return to REDCap via a full-page navigation (not
        // client-side routing). When there's no callback URL (e.g. the local .env dev stand-in),
        // just refresh so the login page renders.
        if (callbackUrl) {
            window.location.href = callbackUrl
            return
        }
        await refresh()
    }, [refresh])

    // Dev-only manual login. If a REDCap authkey is present in sessionStorage (key 'authkey'),
    // validate it via the REAL /login endpoint -- the same path REDCap posts to in prod -- so
    // the PID/username, and therefore the dashboard, resolve dynamically from REDCap even in
    // dev. Otherwise fall back to the configured .env stand-in. Returns whether a session was
    // created.
    const devLogin = useCallback(async () => {
        let ok = false
        try {
            const authKey = typeof window !== 'undefined' ? window.sessionStorage.getItem('authkey') : null
            if (authKey) {
                const response = await fetch('/login', {
                    method: 'POST',
                    body: new URLSearchParams({ AuthKey: authKey }),
                })
                // /login redirects to /login?error=... on failure; fetch follows the redirect,
                // so a clean final URL (no error param) means a session was created.
                ok = response.ok && !new URL(response.url, window.location.origin).searchParams.has('error')
            } else {
                const response = await fetch('/api/auth/dev-login', { method: 'POST' })
                ok = response.ok
            }
        } finally {
            await refresh()
        }
        return ok
    }, [refresh])

    useEffect(() => {
        refresh()
    }, [refresh])

    // The server resolves a dashboard-specific title per project (e.g. "ILPQC PMH Dashboard")
    // once the user is authenticated; apply it to the browser tab so it reflects which
    // Advanced Link the user actually landed on instead of index.html's static default. Before
    // that (loading, or unauthenticated), leave the tab title empty -- the browser then shows
    // the URL itself rather than a placeholder that doesn't match any real dashboard yet.
    useEffect(() => {
        document.title = state.title || ''
    }, [state.title])

    return (
        <AuthContext.Provider value={{ ...state, refresh, logout, devLogin }}>
            {children}
        </AuthContext.Provider>
    )
}
